/* global React, Appear, CaseBreadcrumb, FooterCondensed, LangToggle */

// ─── Helper: renders a paragraph that may have a `strongAt` phrase wrapped
// in <strong>. Pure text fallback otherwise. ───────────────────────────────
function AboutPara({ data }) {
  const { text, strongAt, emphasis } = data;
  if (emphasis) {
    return <p className="about-emphasis">{text}</p>;
  }
  if (strongAt && text.includes(strongAt)) {
    const idx = text.indexOf(strongAt);
    const before = text.slice(0, idx);
    const after = text.slice(idx + strongAt.length);
    return (
      <p>
        {before}
        <strong>{strongAt}</strong>
        {after}
      </p>
    );
  }
  return <p>{text}</p>;
}

// ─── Helper: inserts a handwriting span around `handWord` in a text. ──────
function aperturaLine(line) {
  if (typeof line === "string") return line;
  const { text, handWord } = line;
  if (!handWord || !text.includes(handWord)) return text;
  const idx = text.indexOf(handWord);
  return (
    <React.Fragment>
      {text.slice(0, idx)}
      <span className="case-hand">{handWord}</span>
      {text.slice(idx + handWord.length)}
    </React.Fragment>
  );
}

// ─── About page (second-level) ────────────────────────────────────────────
function AboutPage({ content, lang, setLang, onBack }) {
  const about = content.about;
  return (
    <div className="about-page">
      <header className="process-nav">
        <div className="process-nav-inner shell">
          <CaseBreadcrumb onBackToProjects={onBack} lang={lang} />
          <span className="nav-brand">
            <span className="mark mono">anita<em>.</em>sevilla<em>.</em>ull</span>
          </span>
          <div className="process-nav-end">
            <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>
              {lang === "en" ? "know me better" : "conocerme mejor"}
            </span>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </header>

      {/* APERTURA — dos líneas grandes, mucho aire */}
      <section className="about-apertura shell">
        <span className="tag tag-line">{about.navChip}</span>
        <h1 className="about-apertura-title mono">
          <span className="about-apertura-line1">{aperturaLine(about.apertura.line1)}</span>
          <br />
          <span className="about-apertura-line2">{aperturaLine(about.apertura.line2)}</span>
        </h1>
      </section>

      {about.sections.map((s, i) => (
        <Appear key={i} className="about-section shell">
          <div className="section-head">
            <span className="tag">{s.tag}</span>
          </div>

          {/* Prose común (Quién soy / Cómo trabajo / Comunidad / Lo que no verás) */}
          {s.paragraphs && (
            <div className="about-prose">
              {s.paragraphs.map((p, j) => <AboutPara key={j} data={p} />)}
            </div>
          )}

          {/* Pull-quote — mismo tratamiento que las quotes de reflexión de
              los casos: .case-quote, sin caja, palabra clave en hand. */}
          {s.pullQuote && (() => {
            const { text, handWord } = s.pullQuote;
            const idx = handWord ? text.indexOf(handWord) : -1;
            return (
              <blockquote className="case-quote about-pullquote-flat mono">
                {idx >= 0 ? (
                  <React.Fragment>
                    "{text.slice(0, idx)}
                    <span className="accent">{handWord}</span>
                    {text.slice(idx + handWord.length)}"
                  </React.Fragment>
                ) : (
                  <React.Fragment>"{text}"</React.Fragment>
                )}
              </blockquote>
            );
          })()}
          {s.closingPara && (
            <p className="about-closing">{s.closingPara}</p>
          )}

          {/* Lista limpia de libros */}
          {s.kind === "books" && (
            <React.Fragment>
              {s.intro && <p className="about-books-intro">{s.intro}</p>}
              <ul className="about-books mono">
                {s.books.map((b) => (
                  <li key={b.title}>
                    <span className="about-book-title">{b.title}</span>
                    <span className="about-book-sep" aria-hidden="true"> — </span>
                    <span className="about-book-author">{b.author}</span>
                  </li>
                ))}
              </ul>
            </React.Fragment>
          )}

          {/* Outro (debajo de libros): pitch corto + CTA primario único.
              Cada párrafo puede pedir variant "sans" o "mono". Las líneas
              pueden ser strings o { text, strongAt } para resaltar una
              porción en bold. */}
          {s.outro && (
            <div className="about-outro">
              {s.outro.paragraphs.map((para, pi) => (
                <p
                  key={pi}
                  className={`about-outro-para about-outro-para--${para.variant || "mono"}`}
                >
                  {para.lines.map((line, li) => {
                    const isLast = li === para.lines.length - 1;
                    let node;
                    if (typeof line === "string") {
                      node = line;
                    } else if (line.strongAt && line.text.includes(line.strongAt)) {
                      const idx = line.text.indexOf(line.strongAt);
                      node = (
                        <React.Fragment>
                          {line.text.slice(0, idx)}
                          <strong>{line.strongAt}</strong>
                          {line.text.slice(idx + line.strongAt.length)}
                        </React.Fragment>
                      );
                    } else {
                      node = line.text;
                    }
                    return (
                      <React.Fragment key={li}>
                        {node}
                        {!isLast && <br />}
                      </React.Fragment>
                    );
                  })}
                </p>
              ))}
              {(s.outro.cta || s.outro.ctaSecondary) && (
                <div className="about-outro-ctas">
                  {s.outro.cta && (
                    <a className="btn btn--solid about-outro-cta" href={s.outro.cta.href}>
                      {s.outro.cta.label}
                    </a>
                  )}
                  {s.outro.ctaSecondary && (
                    <a
                      className="btn btn--ghost about-outro-cta-secondary"
                      href={s.outro.ctaSecondary.href}
                      {...(s.outro.ctaSecondary.download ? { download: s.outro.ctaSecondary.download } : {})}
                    >
                      {s.outro.ctaSecondary.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}
        </Appear>
      ))}

      <FooterCondensed content={content} />
    </div>
  );
}

window.AboutPage = AboutPage;
